import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/ui-kit/Reveal";
import { Closing } from "@/components/services/SecurityServiceContent";
import { EASE } from "@/lib/site";

export const CLIENTS = [
  { id: "ananda", name: "Ananda Homes", logo: "/clients/ananda.webp" },
  { id: "cmm", name: "Centre for Migration Medicine", logo: "/clients/cmm.webp" },
  { id: "muppa", name: "Muppa", logo: "/clients/muppa.webp" },
  { id: "niharika", name: "Niharika", logo: "/clients/niharika.webp" },
  { id: "riddhi", name: "Riddhi Builders & Developers", logo: "/clients/riddhi.webp" },
  { id: "aryamitra", name: "Aryamitra Projects", logo: "/clients/aryamitra.webp" },
  { id: "hdfc", name: "HDFC Bank", logo: "/clients/hdfc.webp" },
  { id: "shangrila", name: "Shangrila", logo: "/clients/shangrila.webp" },
  { id: "sriaditya", name: "Sri Aditya Homes Pvt. Ltd.", logo: "/clients/sriaditya.webp" },
  { id: "wildwings", name: "Wild Wings Premium Hotel", logo: "/clients/wildwings.webp" },
  { id: "maarvel", name: "Maarvel Infra Project", logo: "/clients/maarvel.webp" },
];

export const CLIENTS_INTRO = "Some of the organisations that trust S4s Enterprises Pvt. Ltd. with the safety, upkeep and smooth running of their premises.";

const LogoCard = ({ c, i }) => (
  <motion.figure data-testid={`client-logo-${c.id}`} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.8, delay: i * 0.08, ease: EASE }} whileHover={{ y: -6 }}
    className="group relative bg-white chamfer-sm flex flex-col basis-[calc(50%-8px)] md:basis-[calc(33.333%-16px)] lg:basis-[calc(20%-19.2px)] shadow-[0_20px_50px_-30px_rgba(11,15,38,0.35)]">
    <div className="relative aspect-square">
      <img src={c.logo} alt={`${c.name} logo`} loading="lazy" className="absolute inset-0 m-auto max-h-[72%] max-w-[78%] object-contain transition-transform duration-500 group-hover:scale-105" />
    </div>
    <figcaption className="flex-1 border-t border-slate-100 px-5 py-4 text-sm font-semibold text-slate-700 flex items-center gap-3">
      <span className="w-5 h-[3px] bg-[var(--red)] -skew-x-[30deg] transition-[width] duration-500 group-hover:w-8" />{c.name}
    </figcaption>
  </motion.figure>
);

const LogoStrip = () => (
  <section data-testid="clients-logo-marquee" className="marquee-wrap bg-white border-y border-slate-200 py-8 overflow-hidden">
    <div className="marquee-track flex w-max items-center">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0 items-center" aria-hidden={k === 1}>
          {CLIENTS.map((c, j) => (
            <img key={`${c.id}-${j}`} src={c.logo} alt="" className="h-16 sm:h-20 w-auto mx-10 sm:mx-14 object-contain" />
          ))}
        </div>
      ))}
    </div>
  </section>
);

const TESTIMONIALS = [
  { id: "ravi", text: "We rely on S4s Enterprises for combined security, deep cleaning, and event support during our quarterly dealer meets. Their ability to deploy trained, uniformed personnel who take pride in their duties is rare. It feels less like an external agency and more like a dedicated operations partner working alongside us.", name: "Ravi Kumar", role: "Event Managing Executive" },
  { id: "suresh", text: "Maintaining pristine hygiene across high-footfall corporate lobbies, cafeterias, and washrooms used to be a constant struggle until we transitioned to S4s Enterprises. Their housekeeping crew operates on structured SOPs with the right equipment, eco-safe materials, and strict shift discipline. The on-ground team is polite, punctual, and remarkably thorough. Our premises have never looked better.", name: "Suresh", role: "HR, Shangrilla" },
  { id: "ram", text: "Managing security across a multi-Block commercial property requires absolute alertness and clear communication. The security team from S4s Enterprises Pvt. Ltd. has set a whole new benchmark for discipline, access control, and prompt reporting. Knowing that the leadership comes with an ex-serviceman background is evident in how well-trained, proactive, and sharp the guards are on duty. S4s has given our management complete peace of mind.", name: "Ram Kumar", role: "Facility Manager, Niharika" },
];

const Testimonials = () => (
  <section data-testid="clients-testimonials" className="relative bg-white py-20 sm:py-28 overflow-hidden">
    <div aria-hidden className="absolute -left-20 top-24 w-[45%] h-[3px] bg-[#D91219]/70 -rotate-[14deg]" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
      <Reveal>
        <Eyebrow>Testimonials</Eyebrow>
        <h2 className="mt-5 max-w-4xl font-display italic font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] text-[var(--ink)]">
          The gold standard in <span className="text-[var(--navy)]">integrated facility management</span> and <span className="text-[var(--red)]">security.</span>
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.id} delay={i * 0.1} className="h-full">
            <figure data-testid={`testimonial-${t.id}`} className="group h-full flex flex-col bg-[var(--paper)] chamfer-sm p-8 sm:p-10 border-b-[3px] border-transparent hover:border-[var(--red)] transition-colors duration-500">
              <Quote className="w-10 h-10 text-[var(--red)] fill-[var(--red)] transition-transform duration-500 group-hover:-rotate-6" />
              <blockquote className="mt-6 flex-1 text-slate-700 leading-relaxed text-base">“{t.text}”</blockquote>
              <figcaption className="mt-8 pt-6 border-t border-slate-200 flex items-center gap-4">
                <span className="w-12 h-12 shrink-0 grid place-items-center bg-[var(--navy)] text-white font-display italic font-black text-xl">{t.name.charAt(0)}</span>
                <span>
                  <span className="block font-bold text-[var(--ink)]">{t.name}</span>
                  <span className="block text-sm text-slate-600">{t.role}</span>
                </span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function ClientsContent() {
  return (
    <>
      <LogoStrip />
      <section data-testid="clients-gallery" className="bg-[var(--paper)] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal>
            <Eyebrow>Client gallery</Eyebrow>
            <h2 className="mt-5 font-display italic font-black uppercase text-5xl sm:text-6xl leading-[0.9] text-[var(--ink)]">
              Trusted by <span className="text-[var(--red)]">leading names</span>
            </h2>
          </Reveal>
          <div className="mt-14 flex flex-wrap justify-center gap-4 md:gap-6">
            {CLIENTS.map((c, i) => <LogoCard key={c.id} c={c} i={i} />)}
          </div>
        </div>
      </section>
      <Testimonials />
      <Closing testId="clients-closing-cta" lead="Join our" accent="growing client list"
        text="Talk to us about security, housekeeping or event support for your premises." />
    </>
  );
}
